import { atom, useAtom } from 'jotai';
import type { CustomArea, PolygonType } from '../../types/domain';
import { useCustomAreas } from './useCustomAreas';
import { useCallback } from 'react';

type EditingCustomArea = {
  key: string;
  type: PolygonType;
};

type UseCustomAreaEdition = {
  editingCustomArea: EditingCustomArea | null;
  initialCustomArea: CustomArea | null;
  canUndo: boolean;
  startCustomAreaEdition: (customArea: CustomArea) => void;
  endCustomAreaEdition: () => void;
  editCustomArea: (
    changes: Partial<Omit<CustomArea, 'key'>>,
    saveHistory?: boolean
  ) => void;
  undoCustomArea: () => void;
  resetCustomArea: () => void;
};

const editingCustomAreaAtom = atom<EditingCustomArea | null>(null);

const initialCustomAreaAtom = atom<CustomArea | null>(null);

const customAreaHistoryAtom = atom<CustomArea[]>([]);

const useCustomAreaEdition = (): UseCustomAreaEdition => {
  const [editingCustomArea, setEditingCustomArea] = useAtom(
    editingCustomAreaAtom
  );
  const [customAreaHistory, setCustomAreaHistory] = useAtom(
    customAreaHistoryAtom
  );
  const [initialCustomArea, setInitialCustomArea] = useAtom(
    initialCustomAreaAtom
  );
  const { updateCustomArea } = useCustomAreas();

  const canUndo = editingCustomArea !== null && customAreaHistory.length > 1;

  const startCustomAreaEdition = useCallback(
    (customArea: CustomArea): void => {
      setEditingCustomArea({ key: customArea.key, type: customArea.type });
      setCustomAreaHistory([customArea]);
      setInitialCustomArea(customArea);
    },
    [setEditingCustomArea, setCustomAreaHistory, setInitialCustomArea]
  );

  const endCustomAreaEdition = useCallback((): void => {
    if (!editingCustomArea) {
      return;
    }
    setEditingCustomArea(null);
    setCustomAreaHistory([]);
    setInitialCustomArea(null);
  }, [
    editingCustomArea,
    setEditingCustomArea,
    setCustomAreaHistory,
    setInitialCustomArea,
  ]);

  const editCustomArea = useCallback(
    (
      changes: Partial<Omit<CustomArea, 'key'>>,
      saveHistory: boolean = true
    ): void => {
      if (!editingCustomArea) {
        return;
      }

      updateCustomArea(editingCustomArea.key, changes);
      if (changes.type && changes.type !== editingCustomArea.type) {
        setEditingCustomArea({ ...editingCustomArea, type: changes.type });
      }
      if (saveHistory) {
        // 100アクションまで戻せるように履歴を保存する
        setCustomAreaHistory((currentHistory: CustomArea[]): CustomArea[] => {
          const lastHistory =
            currentHistory[currentHistory.length - 1] ?? initialCustomArea;
          return [...currentHistory.slice(-99), { ...lastHistory, ...changes }];
        });
      }
    },
    [
      editingCustomArea,
      initialCustomArea,
      setEditingCustomArea,
      setCustomAreaHistory,
      updateCustomArea,
    ]
  );

  const undoCustomArea = useCallback((): void => {
    if (!editingCustomArea || customAreaHistory.length <= 1) {
      return;
    }

    // 最新の履歴は現在と同じ状態であるため、2つ前の状態に戻す必要がある
    const targetState = customAreaHistory[customAreaHistory.length - 2];

    updateCustomArea(editingCustomArea.key, targetState);
    if (targetState.type !== editingCustomArea.type) {
      setEditingCustomArea({ ...editingCustomArea, type: targetState.type });
    }
    setCustomAreaHistory((currentHistory: CustomArea[]): CustomArea[] => {
      return currentHistory.slice(0, -1);
    });
  }, [
    editingCustomArea,
    customAreaHistory,
    setEditingCustomArea,
    setCustomAreaHistory,
    updateCustomArea,
  ]);

  const resetCustomArea = useCallback((): void => {
    if (!editingCustomArea || !initialCustomArea) {
      return;
    }
    updateCustomArea(editingCustomArea.key, initialCustomArea);
    if (initialCustomArea.type !== editingCustomArea.type) {
      setEditingCustomArea({
        ...editingCustomArea,
        type: initialCustomArea.type,
      });
    }
    setCustomAreaHistory([initialCustomArea]);
  }, [
    editingCustomArea,
    initialCustomArea,
    setEditingCustomArea,
    updateCustomArea,
    setCustomAreaHistory,
  ]);

  return {
    editingCustomArea,
    initialCustomArea,
    canUndo,
    startCustomAreaEdition,
    endCustomAreaEdition,
    editCustomArea,
    undoCustomArea,
    resetCustomArea,
  };
};

export { useCustomAreaEdition };
